<template>
    <div ref="filterContainer">
        <div class="subfilter-header" v-if="title || selectAllSwitch">
            <div class="title" v-if="title">
                {{ title }}
            </div>
            <div
                class="select-all-switch"
                v-if="selectAllSwitch"
                @click="isSelectedAll ? unselectAll() : selectAll()"
            >
                <span v-show="!isSelectedAll">{{
                    $trans("web_search.filters.select_all")
                }}</span>
                <span v-show="isSelectedAll">{{
                    $trans("web_search.filters.unselect_all")
                }}</span>
                <el-checkbox v-model="isSelectedAll" />
            </div>
        </div>

        <div v-if="Object.entries(options).length">
            <div
                class="search-section-wrapper"
                v-if="searchbar || modifierSwitch"
            >
                <div class="search-bar-wrapper" v-if="searchbar">
                    <el-input
                        v-model="searchInput"
                        :placeholder="$trans('web_search.filters.search')"
                        clearable
                    >
                        <template #suffix>
                            <img src="/media/images/search_gray.png" />
                        </template>
                    </el-input>
                </div>

                <el-switch
                    v-if="modifierSwitch"
                    v-model="modifier"
                    inline-prompt
                    :active-text="$trans('web_search.filters.and')"
                    :inactive-text="$trans('web_search.filters.or')"
                    active-value="AND"
                    inactive-value="OR"
                />
            </div>

            <div
                v-for="(option, loopIndex) in filteredOptions"
                :key="option.id"
            >
                <el-checkbox
                    :data-id="option.id"
                    v-model="storeSelectedFilters[filterKey][option.id]"
                    v-show="showOption(option, loopIndex)"
                >
                    <span class="option-label">
                        {{ option.value }}
                        <span
                            v-if="shouldShowOccurrences(option)"
                            class="occurrences-count"
                        >
                            ({{ option.occurrences }})
                        </span>
                    </span>
                </el-checkbox>
            </div>

            <div
                class="show-more-button"
                @click="loadMore"
                v-if="!autoShowMore && filteredOptions.length > currentLimit"
            >
                {{ $trans("web_search.filters.show_more") }}
            </div>
        </div>

        <div v-else>
            <i>{{ $trans("web_search.filters.no_options") }}</i>
        </div>
    </div>
</template>

<script>
import { Search as SearchIcon } from "@element-plus/icons-vue";
import { mapStores, mapState } from "pinia";
import { useSelectedFiltersStore } from "@/stores/SelectedFiltersStore";

export default {
    components: {
        SearchIcon,
    },

    props: {
        filterKey: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            required: false,
            default: "",
        },
        searchbar: {
            type: Boolean,
            required: false,
            default: false,
        },
        modifierSwitch: {
            type: Boolean,
            required: false,
            default: false,
        },
        modifierState: {
            type: String,
            required: false,
            default: "OR",
        },
        options: {
            type: Object,
            required: false,
        },
        selectAllSwitch: {
            type: Boolean,
            required: false,
            default: false,
        },
        maxLength: {
            type: Number,
            required: false,
            default: 6,
        },
    },

    data() {
        return {
            currentLimit: this.maxLength,
            modifier: "AND",
            searchInput: "",
            selectAllSwitchState: "",
            resizeObserver: null,
        };
    },

    created() {
        this.SelectedFiltersStore.registerModifier(
            this.filterKey,
            this.modifier,
        );
    },

    mounted() {
        this.modifier = this.modifierState;

        this.resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.contentRect.height === 0) {
                    this.currentLimit = this.maxLength;
                }
            }
        });

        this.$nextTick(() => {
            if (this.$refs.filterContainer) {
                this.resizeObserver.observe(this.$refs.filterContainer);
            }
        });
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        showOption(option, loopIndex) {
            return this.autoShowMore || loopIndex < this.currentLimit;
        },

        loadMore() {
            this.currentLimit += 20;
        },

        selectAll() {
            let option;
            for (option of this.options) {
                this.SelectedFiltersStore.setState(
                    this.filterKey,
                    option.id,
                    true,
                );
            }
        },

        unselectAll() {
            let option;
            for (option of this.options) {
                this.SelectedFiltersStore.setState(
                    this.filterKey,
                    option.id,
                    false,
                );
            }
        },

        shouldShowOccurrences(option) {
            return (
                (this.filterKey === "competencies" ||
                    this.filterKey === "industrySectors") &&
                option.occurrences !== undefined &&
                option.occurrences !== null
            );
        },
    },

    computed: {
        ...mapStores(useSelectedFiltersStore),

        ...mapState(useSelectedFiltersStore, {
            storeSelectedFilters: "selectedFilters",
        }),

        autoShowMore() {
            return this.maxLength == -1;
        },

        isSelectedAll() {
            for (let option of this.options) {
                let state =
                    this.storeSelectedFilters[this.filterKey][option.id];
                if (state == false || state == undefined) {
                    return false;
                }
            }
            return true;
        },

        filteredOptions() {
            let searchInput = this.searchInput.toLowerCase();

            let fitleredOptions = this.options.filter((option) => {
                return option.value.toLowerCase().indexOf(searchInput) > -1;
            });

            return fitleredOptions;
        },
    },

    watch: {
        modifier: function () {
            this.SelectedFiltersStore.setModifier(
                this.filterKey,
                this.modifier,
            );
        },
    },
};
</script>

<style scoped>
.option-label {
    display: flex;
    align-items: center;
}

.occurrences-count {
    color: #999;
    font-size: 0.9em;
    margin-left: 4px;
}

.subfilter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #bababa;
    padding: 0 10px;
}

.subfilter-header .select-all-switch {
    margin-left: auto;
    display: flex;
    align-items: center;
    text-wrap: nowrap;
    gap: 10px;
    white-space: nowrap;
    padding-left: 5px;
}

.select-all-switch .el-checkbox:deep(.el-checkbox__inner) {
    border-radius: 50%;
    border-color: #707070;
    height: 20px;
    width: 20px;
}

.select-all-switch
    .el-checkbox:deep(
        .el-checkbox__input.is-checked .el-checkbox__inner::after
    ) {
    display: none;
}

.select-all-switch
    .el-checkbox:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-image:
        linear-gradient(
            to bottom,
            var(--el-checkbox-checked-bg-color) 0%,
            var(--el-checkbox-checked-bg-color) 100%
        ),
        linear-gradient(to bottom, #fff 0%, #fff 100%);
    padding: 3px;
    background-clip: content-box, padding-box;
}

.show-more-button {
    cursor: pointer;
    font-size: 14px;
    text-align: right;
    margin-right: 15px;
    color: #41abc4;
}

.show-more-button:hover {
    color: #2e7282;
}

.el-checkbox {
    --el-checkbox-font-size: 14px;
    --el-checkbox-font-weight: var(--el-font-weight-primary);
    --el-checkbox-text-color: #3c3c3c;
    --el-checkbox-input-height: 14px;
    --el-checkbox-input-width: 14px;
    --el-checkbox-border-radius: 0;
    --el-checkbox-bg-color: white;
    --el-checkbox-input-border: 1px solid #41abc4;
    --el-checkbox-disabled-border-color: var(--el-border-color);
    --el-checkbox-disabled-input-fill: var(--el-fill-color-light);
    --el-checkbox-disabled-icon-color: var(--el-text-color-placeholder);
    --el-checkbox-disabled-checked-input-fill: var(
        --el-border-color-extra-light
    );
    --el-checkbox-disabled-checked-input-border-color: var(--el-border-color);
    --el-checkbox-disabled-checked-icon-color: var(--el-text-color-placeholder);
    --el-checkbox-checked-text-color: #3c3c3c;
    --el-checkbox-checked-input-border-color: #41abc4;
    --el-checkbox-checked-bg-color: #41abc4;
    --el-checkbox-checked-icon-color: var(--el-color-white);
    --el-checkbox-input-border-color-hover: #41abc4;

    height: 24px;
}

.el-input {
    --el-input-text-color: var(--el-text-color-regular);
    --el-input-border: #3c3c3c;
    --el-input-hover-border: #3c3c3c;
    --el-input-focus-border: #3c3c3c;
    --el-input-transparent-border: 0 0 0 1px transparent inset;
    --el-input-border-color: var(--el-border-color);
    --el-input-border-radius: var(--el-border-radius-base);
    --el-input-bg-color: var(--el-fill-color-blank);
    --el-input-icon-color: #a5a5a5;
    --el-input-placeholder-color: #7b7b7b;
    --el-input-hover-border-color: #3c3c3c;
    --el-input-clear-hover-color: var(--el-text-color-secondary);
    --el-input-focus-border-color: #3c3c3c;
    /* --el-input-width: 200px; */
}

.el-switch,
.el-switch :deep(.el-switch__core) {
    height: 22px;
}

.el-switch {
    --el-switch-on-color: #41abc4;
    --el-switch-off-color: #707070;
    border-radius: 24px;
}

.el-switch :deep(.el-switch__core .el-switch__inner .is-text) {
    padding: 0 4px;
}

.el-icon,
.el-icon :deep(svg) {
    height: 1.5em;
    width: 1.5em;
}

.el-checkbox {
    width: 100%;
}

.el-checkbox :deep(.el-checkbox__label) {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    line-height: 30px;
}

.search-section-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
}
.search-bar-wrapper {
    flex-grow: 1;
    line-height: 1;
}
.search-bar-wrapper .el-input {
    --el-input-height: 26px;
    vertical-align: unset;
}
.search-bar-wrapper .el-input__wrapper:hover {
    box-shadow: unset;
}
.search-bar-wrapper .el-input:deep(.el-input__wrapper) {
    border-radius: 5px;
    border: 1px solid #747474;
    height: 22px;
}
.search-bar-wrapper img {
    position: relative;
    width: 24px;
    top: 1px;
}

@media only screen and (max-width: 790px) {
    .el-checkbox :deep(.el-checkbox__label) {
        font-size: 15px;
        line-height: 30px;
    }

    .el-checkbox :deep(.el-checkbox__inner) {
        width: 25px;
        height: 25px;
    }

    .el-checkbox :deep(.el-checkbox__inner::after) {
        height: 12px;
        width: 5px;
        left: 11px;
        top: 11px;
    }

    .subfilter-header {
        margin-bottom: 10px;
    }
}
</style>
